import { useSyncExternalStore } from 'react';

import { DataTestIds } from '@repo/frontend-shared';
import type { CropMeta } from '@repo/ui/components/elements/Cropper/Cropper';

import type { ImageData, ImageInputProps, ResolvedImageInputProps } from './ImageInput';

interface ObjectUrlStore {
  getSnapshot: () => string | null;
  subscribe: (onStoreChange: () => void) => () => void;
}

const emptyObjectUrlStore: ObjectUrlStore = {
  getSnapshot: () => null,
  subscribe: () => () => {},
};
const objectUrlStores = new WeakMap<File, ObjectUrlStore>();

function getObjectUrlStore(file: File): ObjectUrlStore {
  const existingStore = objectUrlStores.get(file);
  if (existingStore) {
    return existingStore;
  }

  let objectUrl: string | null = null;
  const subscribers = new Set<() => void>();
  const store: ObjectUrlStore = {
    getSnapshot: () => objectUrl,
    subscribe: onStoreChange => {
      subscribers.add(onStoreChange);
      if (!objectUrl) {
        objectUrl = URL.createObjectURL(file);
      }
      onStoreChange();

      return () => {
        subscribers.delete(onStoreChange);
        if (subscribers.size === 0 && objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };
    },
  };
  objectUrlStores.set(file, store);
  return store;
}

export function useObjectUrl(file: File | undefined): string | null {
  const store = file ? getObjectUrlStore(file) : emptyObjectUrlStore;
  return useSyncExternalStore(store.subscribe, store.getSnapshot, emptyObjectUrlStore.getSnapshot);
}

interface CommittedImage {
  thumbnailUrl?: string | null;
  originalUrl?: string | null;
  thumbnailId?: string | number;
  originalId?: string | number;
  meta?: CropMeta | null;
}

export function createCommittedImageData(picture: CommittedImage | null | undefined): ImageData {
  return {
    meta: picture?.meta ?? undefined,
    url: picture?.thumbnailUrl ?? undefined,
    originalUrl: picture?.originalUrl ?? undefined,
    originalId: picture?.originalId === undefined ? undefined : String(picture.originalId),
    thumbnailId: picture?.thumbnailId === undefined ? undefined : String(picture.thumbnailId),
  };
}

export function getOriginalUrl(value: ImageData | null): string | undefined {
  const url = value?.url;
  if (!url) {
    return undefined;
  }

  return url.startsWith('http') || url.startsWith('/') ? url : `/${url}`;
}

export function resolveImageInputProps(props: ImageInputProps): ResolvedImageInputProps {
  return {
    ...props,
    displayName: props.displayName ?? 'Add a Thumbnail',
    previewDataTestId: props.previewDataTestId ?? DataTestIds.COMMON.IMAGE_INPUT_PREVIEW,
    // The edit and remove controls carry the shared contract ids unless a
    // consumer names its own, exactly like the preview and the error message.
    // Without a default they rendered with no id at all for every consumer that
    // does not pass one — which is every admin editor, because they all reach
    // this component through FormImageInput.
    editDataTestId: props.editDataTestId ?? DataTestIds.COMMON.IMAGE_INPUT_EDIT,
    removeDataTestId: props.removeDataTestId ?? DataTestIds.COMMON.IMAGE_INPUT_REMOVE,
    previewAlt: props.previewAlt ?? 'Uploaded image',
    allowRemove: props.allowRemove ?? true,
    allowCommittedRemove: props.allowCommittedRemove ?? false,
    isDisabled: props.isDisabled ?? false,
    errorDataTestId: props.errorDataTestId ?? DataTestIds.COMMON.IMAGE_INPUT_ERROR,
    presentation: props.presentation ?? 'default',
  };
}

export function canRemoveImage(value: ImageData | null, allowRemove: boolean, allowCommittedRemove: boolean): boolean {
  return allowRemove && (!value?.originalId || allowCommittedRemove);
}

export function hasEditableOriginal(value: ImageData | null): boolean {
  return Boolean(value?.originalUrl || value?.original);
}

export function resolveCropperImageUrl(previewUrl: string | null, value: ImageData | null): string {
  return previewUrl || value?.originalUrl || '';
}

export function canCommitCrop(originalImage: File | undefined, value: ImageData | null): boolean {
  return Boolean(originalImage || value?.originalId);
}

export function buildCropCommitPayload(
  originalImage: File | undefined,
  thumbnailFile: File,
  meta: CropMeta,
  value: ImageData | null,
): ImageData {
  return {
    ...(originalImage ? { original: originalImage } : {}),
    thumbnail: thumbnailFile,
    meta,
    ...(value?.originalUrl ? { originalUrl: value.originalUrl } : {}),
    ...(value?.originalId ? { originalId: value.originalId } : {}),
    ...(value?.thumbnailId ? { thumbnailId: value.thumbnailId } : {}),
  };
}
